import { useCallback, useEffect, useState } from 'react';
import {
  CONTENT_CONFIGS,
  fetchContentRows,
  deleteContentRow,
  updateContentRow,
  type ContentConfig,
  type ContentRow,
} from '@/lib/content';

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="mb-6">
      <h1 className="font-heading font-bold text-2xl text-foreground-50">{title}</h1>
      <p className="text-sm text-foreground-400 mt-1">{subtitle}</p>
    </div>
  );
}

function formatValue(row: ContentRow, key: string, kind?: string): string {
  const raw = row[key];
  if (raw === null || raw === undefined || raw === '') return '—';
  if (kind === 'money' && typeof raw === 'number') {
    return `${raw.toLocaleString('fr-FR')} FCFA`;
  }
  return String(raw);
}

function StatusPill({ value }: { value: string }) {
  const active = value === 'active' || value === 'approved';
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
        active
          ? 'bg-secondary-500/20 text-secondary-300'
          : 'bg-background-200/60 text-foreground-500'
      }`}
    >
      {value || '—'}
    </span>
  );
}

function ContentSection() {
  const [activeTab, setActiveTab] = useState(0);
  const [rows, setRows] = useState<ContentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const [editing, setEditing] = useState<ContentRow | null>(null);
  const [editForm, setEditForm] = useState<Record<string, string>>({});
  const [deleting, setDeleting] = useState<ContentRow | null>(null);

  const config = CONTENT_CONFIGS[activeTab];

  const load = useCallback(async (cfg: ContentConfig) => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchContentRows(cfg.table);
      setRows(data);
    } catch {
      setError("Impossible de charger ce contenu. Vérifiez vos droits d'administrateur.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(config);
  }, [activeTab, config, load]);

  const openEdit = (row: ContentRow) => {
    const form: Record<string, string> = {};
    config.editFields.forEach((f) => {
      const val = row[f.key];
      form[f.key] = val === null || val === undefined ? '' : String(val);
    });
    setEditForm(form);
    setEditing(row);
    setError('');
    setSuccess('');
  };

  const submitEdit = async () => {
    if (!editing) return;
    setBusyId(editing.id as string);
    setError('');
    setSuccess('');
    try {
      const fields: Record<string, unknown> = {};
      config.editFields.forEach((f) => {
        let val: unknown = editForm[f.key];
        if (val === '' || val === null) {
          val = null;
        } else if (f.kind === 'number') {
          val = Number(val);
        }
        fields[f.key] = val;
      });
      await updateContentRow(config.table, editing.id as string, fields);
      setEditing(null);
      setSuccess('Contenu mis à jour avec succès.');
      await load(config);
    } catch {
      setError("Impossible de modifier ce contenu. Vérifiez vos droits d'administrateur.");
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusyId(deleting.id as string);
    setError('');
    setSuccess('');
    try {
      await deleteContentRow(config.table, deleting.id as string);
      setRows((prev) => prev.filter((r) => r.id !== deleting.id));
      setDeleting(null);
      setSuccess('Contenu supprimé.');
    } catch {
      setError('Impossible de supprimer ce contenu.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <SectionHeader
        title="Gestion des contenus"
        subtitle="Consulter, modifier et supprimer tous les contenus publiés sur la plateforme."
      />

      {/* Onglets par type de contenu */}
      <div className="flex flex-wrap gap-2 mb-6">
        {CONTENT_CONFIGS.map((cfg, idx) => (
          <button
            key={cfg.table}
            onClick={() => {
              setActiveTab(idx);
              setRows([]);
            }}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === idx
                ? 'bg-primary-500 text-foreground-950'
                : 'bg-background-100 border border-background-200/70 text-foreground-400 hover:text-foreground-50'
            }`}
          >
            <i className={`${cfg.icon} text-base`} />
            {cfg.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-accent-100/10 border border-accent-400/20 mb-4">
          <i className="ri-error-warning-line text-accent-400 mt-0.5 shrink-0" />
          <p className="text-sm text-accent-400">{error}</p>
        </div>
      )}
      {success && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-secondary-500/10 border border-secondary-300/20 mb-4">
          <i className="ri-checkbox-circle-line text-secondary-300 mt-0.5 shrink-0" />
          <p className="text-sm text-secondary-300">{success}</p>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-foreground-400">
          {rows.length} élément{rows.length > 1 ? 's' : ''}
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-foreground-400">
          <i className="ri-loader-4-line animate-spin text-2xl mr-2" />
          Chargement…
        </div>
      ) : rows.length === 0 ? (
        <p className="text-foreground-400">Aucun contenu trouvé.</p>
      ) : (
        <div className="bg-background-100 border border-background-200/70 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200/70 text-left text-foreground-400">
                  {config.columns.map((c) => (
                    <th key={c.key} className="px-4 py-3 font-medium">
                      {c.label}
                    </th>
                  ))}
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id as string}
                    className="border-b border-background-200/50 hover:bg-background-200/30 transition-colors"
                  >
                    {config.columns.map((c) => (
                      <td key={c.key} className="px-4 py-3">
                        {c.kind === 'status' ? (
                          <StatusPill value={String(row[c.key] ?? '')} />
                        ) : c.key === config.titleKey ? (
                          <span className="font-medium text-foreground-50 line-clamp-1 max-w-xs">
                            {formatValue(row, c.key)}
                          </span>
                        ) : (
                          <span className="text-foreground-500 line-clamp-1 max-w-xs">
                            {formatValue(row, c.key, c.kind)}
                          </span>
                        )}
                      </td>
                    ))}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => openEdit(row)}
                          title="Modifier"
                          className="w-8 h-8 flex items-center justify-center rounded-md bg-background-200/60 text-foreground-400 hover:text-foreground-50 hover:bg-background-300/60 transition-colors"
                        >
                          <i className="ri-edit-line text-sm" />
                        </button>
                        <button
                          onClick={() => setDeleting(row)}
                          title="Supprimer"
                          className="w-8 h-8 flex items-center justify-center rounded-md bg-foreground-800/80 text-foreground-100 hover:bg-foreground-700 transition-colors"
                        >
                          <i className="ri-delete-bin-6-line text-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de modification */}
      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onClick={() => setEditing(null)}
        >
          <div
            className="w-full max-w-lg max-h-[85vh] overflow-y-auto bg-background-50 border border-background-200/70 rounded-xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading font-semibold text-lg text-foreground-50">
                Modifier le contenu
              </h2>
              <button
                onClick={() => setEditing(null)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-200/60 text-foreground-500 transition-colors"
                aria-label="Fermer"
              >
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="space-y-4">
              {config.editFields.map((f) => (
                <div key={f.key}>
                  <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                    {f.label}
                  </label>
                  {f.kind === 'textarea' ? (
                    <textarea
                      value={editForm[f.key] ?? ''}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      rows={4}
                      className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 resize-none"
                    />
                  ) : (
                    <input
                      type={f.kind === 'number' ? 'number' : 'text'}
                      value={editForm[f.key] ?? ''}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 rounded-md text-sm font-medium text-foreground-400 hover:text-foreground-50 hover:bg-background-200/60 transition-colors whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                onClick={submitEdit}
                disabled={busyId === editing.id}
                className="px-4 py-2 rounded-md bg-primary-500 text-foreground-950 text-sm font-semibold hover:bg-primary-400 disabled:opacity-50 transition-colors whitespace-nowrap flex items-center gap-1.5"
              >
                {busyId === editing.id ? (
                  <i className="ri-loader-4-line animate-spin" />
                ) : (
                  <i className="ri-check-line" />
                )}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de suppression */}
      {deleting && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onClick={() => setDeleting(null)}
        >
          <div
            className="w-full max-w-sm bg-background-50 border border-background-200/70 rounded-xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500/10 mx-auto mb-4">
              <i className="ri-alert-line text-2xl text-accent-400" />
            </div>
            <h2 className="font-heading font-semibold text-lg text-foreground-50 text-center">
              Supprimer ce contenu ?
            </h2>
            <p className="text-sm text-foreground-400 text-center mt-2">
              « {String(deleting[config.titleKey] ?? '')} » sera définitivement supprimé. Cette
              action est irréversible.
            </p>
            <div className="flex justify-center gap-2 mt-6">
              <button
                onClick={() => setDeleting(null)}
                className="px-4 py-2 rounded-md text-sm font-medium text-foreground-400 hover:text-foreground-50 hover:bg-background-200/60 transition-colors whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                onClick={confirmDelete}
                disabled={busyId === deleting.id}
                className="px-4 py-2 rounded-md bg-foreground-800 text-foreground-100 text-sm font-semibold hover:bg-foreground-700 disabled:opacity-50 transition-colors whitespace-nowrap flex items-center gap-1.5"
              >
                {busyId === deleting.id ? (
                  <i className="ri-loader-4-line animate-spin" />
                ) : (
                  <i className="ri-delete-bin-6-line" />
                )}
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContentSection;